import { computed, toValue, type MaybeRefOrGetter } from 'vue';

export type ApartmentFactsInput = {
  capacity?: number | string | null;
  rooms?: number | string | null;
  floor?: number | string | null;
  view_type?: 'sea' | 'panoramic' | 'mountain' | null;
  area_sqm?: number | null;
};

export type GuestFactsInput = {
  capacity: number;
  threshold: number;
  extraGuestUsd?: number;
} | null | undefined;

const HIGH_FLOOR_THRESHOLD = 13;

export function useApartmentFacts(
  apartment: MaybeRefOrGetter<ApartmentFactsInput>,
  guestFacts: MaybeRefOrGetter<GuestFactsInput>,
) {
  const { t } = useI18n();

  const capacityNum = computed(() => {
    const n = Number(toValue(apartment).capacity);
    return Number.isFinite(n) && n > 0 ? n : null;
  });

  const baseGuests = computed(() => {
    const thr = toValue(guestFacts)?.threshold;
    return Number.isFinite(thr) && (thr as number) > 0
      ? (thr as number)
      : capacityNum.value;
  });

  const extraGuests = computed(() => {
    const facts = toValue(guestFacts);
    const cap = facts?.capacity ?? capacityNum.value;
    const thr = facts?.threshold;
    if (!Number.isFinite(cap) || !Number.isFinite(thr)) return 0;
    return Math.max(0, (cap as number) - (thr as number));
  });

  const totalGuests = computed(() => {
    if (!baseGuests.value) return null;
    return baseGuests.value + extraGuests.value;
  });

  const extraGuestUsd = computed(() => {
    const usd = toValue(guestFacts)?.extraGuestUsd;
    return Number.isFinite(usd) && (usd as number) > 0 ? (usd as number) : null;
  });

  const roomsNum = computed(() => {
    const n = Number(toValue(apartment).rooms);
    return Number.isFinite(n) && n > 0 ? n : null;
  });

  const floorNum = computed(() => {
    const n = Number(toValue(apartment).floor);
    return Number.isFinite(n) && n > 0 ? n : null;
  });

  const isHighFloor = computed(
    () => floorNum.value != null && floorNum.value >= HIGH_FLOOR_THRESHOLD,
  );

  const viewLabel = computed(() => {
    const v = toValue(apartment).view_type;
    if (!v) return null;
    return t(`apartment.facts.view.${v}`);
  });

  // Marketing rounding for card surfaces. Detail page (ApartmentFacts.vue) and
  // JSON-LD structured data keep the raw area_sqm.
  const areaSqmRounded = computed(() => {
    const n = Number(toValue(apartment).area_sqm);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.ceil(n / 5) * 5;
  });

  return {
    capacityNum,
    baseGuests,
    extraGuests,
    totalGuests,
    extraGuestUsd,
    roomsNum,
    floorNum,
    isHighFloor,
    viewLabel,
    areaSqmRounded,
  };
}
